/**
 * This component inherited from `rc-cascader`,
 * all the component related to this one needed to be tested before you upgrade `rc-cascader`.
 */
import React, { cloneElement } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import Trigger from 'rc-trigger'
import RcCascader from 'rc-cascader'
import styles from './index.css'
import { Menus as CascaderMenus } from '@vzmi/shp_shared_compo'
import { MARK_AS_HIDE } from '../../../constants/proposalTypes'
import _ from 'lodash'

const BUILT_IN_PLACEMENTS = {
  topLeftClose: {
    points: ['bl', 'tl'],
    offset: [0, 0],
    overflow: {
      adjustX: 1,
      adjustY: 1
    }
  },
  bottomLeftClose: {
    points: ['tl', 'bl'],
    offset: [0, 0],
    overflow: {
      adjustX: 1,
      adjustY: 1
    }
  },
  bottomLeft: {
    points: ['tl', 'bl'],
    offset: [0, 4],
    overflow: {
      adjustX: 1,
      adjustY: 1
    }
  },
  topLeft: {
    points: ['bl', 'tl'],
    offset: [0, -4],
    overflow: {
      adjustX: 1,
      adjustY: 1
    }
  },
  bottomRight: {
    points: ['tr', 'br'],
    offset: [0, 4],
    overflow: {
      adjustX: 1,
      adjustY: 1
    }
  },
  topRight: {
    points: ['br', 'tr'],
    offset: [0, -4],
    overflow: {
      adjustX: 1,
      adjustY: 1
    }
  }
}

/**
 *
 * @param {*} options 選單項目
 * @param {*} suffixAsInvisible 是否對隱藏選單顯示"(暫隱形)", true: 顯示, false: 選項整個消失不顯示
 */
const filterOptions = (options, suffixAsInvisible) => {
  const shouldHide = (element) => {
    /* for debug
    return element.label.includes('專用父類') ||
      element.label.includes('專用子類') ||
      element.label.includes('13吋') ||
      element.label.includes('遊戲軟體')
    */
    return (element.visible === false)
  }

  let op = null
  if (suffixAsInvisible) { // menu option 此項目加上"(暫隱形)"的字尾
    op = _.map(options, (element) => {
      if (shouldHide(element) && !element.label.includes(MARK_AS_HIDE)) {
        element.label = element.label + MARK_AS_HIDE
      }
      if (element.children) {
        element.children = filterOptions(element.children, suffixAsInvisible)
      }
      return element
    })
  } else { // menu option 此項目整個移除不顯示
    op = _.reduce(options, (result, element) => {
      if (!shouldHide(element)) {
        if (element.children) {
          element.children = filterOptions(element.children, suffixAsInvisible)
        }
        result.push(element)
      }
      return result
    }, [])
  }

  return op
}

export const generateCascaderOptions = (options, config) => {
  const {
    withAllOption,
    isLastLayer
  } = config
  // 項目排序按照項目名稱
  let currentOptions = _.sortBy(options, 'label')
  if (withAllOption) {
    currentOptions = _.concat({ label: '全部', value: 'all' }, currentOptions)
  }
  const cascaderOptions = _.map(currentOptions, option => {
    return {
      ...option,
      // 決定顯示項目目前是否在載入下一層的資料
      loading: false,
      // true 不顯示可以展開下一層的 icon，點選項目會直接當作選擇項目
      isLeaf: (
        isLastLayer ||
        option.value === 'all'
      ),
      // 舊資料不會補上父類所以不能查不顯示
      // https://docs.google.com/document/d/1Tti28mGYcrgrqDibJN_h1aCMnizBRYfshk8gI0E9Im0/edit#heading=h.cxi7yazbc6y5
      visible: option.value !== 'other'
    }
  })
  return cascaderOptions
}

class CommonCascader extends RcCascader {
  render () {
    const {
      prefixCls,
      transitionName,
      containerClassName,
      popupClassName,
      disabled,
      builtinPlacements,
      popupPlacement,
      selectOnSearch,
      options,
      isFilterOptions,
      suffixAsInvisible,
      children,
      ...restProps
    } = this.props
    let filteredOptions = options
    if (isFilterOptions) {
      filteredOptions = filterOptions(options, suffixAsInvisible)
    }

    // Did not show popup when there is no options
    let menus = <div />
    let emptyMenuClassName = ''
    if (filteredOptions && filteredOptions.length > 0) {
      menus = (
        <CascaderMenus
          {...this.props}
          options={filteredOptions}
          fieldNames={this.getFieldNames()}
          defaultFieldNames={this.defaultFieldNames}
          popupPlacement={popupPlacement}
          activeValue={this.state.activeValue}
          selectOnSearch={selectOnSearch}
          onSelect={this.handleMenuSelect}
          visible={this.state.popupVisible}
        />
      )
    } else {
      emptyMenuClassName = ` ${prefixCls}-menus-empty`
    }
    return (
      <>
        <section>
          <div
            className={cx(
              styles.CascaderContainer,
              { [containerClassName]: containerClassName }
            )}
            ref={r => (this.categoryCascaderContainer = r)}
          />
        </section>
        <section>
          <Trigger
            ref={this.saveTrigger}
            {...restProps}
            options={filteredOptions}
            disabled={disabled}
            popupPlacement={popupPlacement}
            builtinPlacements={builtinPlacements}
            popupTransitionName={transitionName}
            action={disabled ? [] : ['click']}
            popupVisible={disabled ? false : this.state.popupVisible}
            onPopupVisibleChange={this.handlePopupVisibleChange}
            prefixCls={`${prefixCls}-menus`}
            popupClassName={`${popupClassName + emptyMenuClassName}`}
            popup={menus}
            getPopupContainer={() => this.categoryCascaderContainer}
          >
            {cloneElement(children, {
              active: this.state.popupVisible,
              onKeyDown: this.handleKeyDown,
              tabIndex: disabled ? undefined : 0
            })}
          </Trigger>
        </section>
      </>
    )
  }
}

CommonCascader.defaultProps = {
  options: [],
  onChange () {},
  onPopupVisibleChange () { },
  menuInputPlaceholder: [],
  disabled: false,
  transitionName: 'slide-up',
  prefixCls: 'y-cascader',
  popupClassName: '',
  popupPlacement: 'bottomLeftClose',
  builtinPlacements: BUILT_IN_PLACEMENTS,
  expandTrigger: 'click',
  fieldNames: { label: 'label', value: 'value', children: 'children' }
}

CommonCascader.propTypes = {
  value: PropTypes.array,
  defaultValue: PropTypes.array,
  menuInputPlaceholder: PropTypes.array,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  onPopupVisibleChange: PropTypes.func,
  popupVisible: PropTypes.bool,
  disabled: PropTypes.bool,
  transitionName: PropTypes.string,
  popupClassName: PropTypes.string,
  popupPlacement: PropTypes.string,
  prefixCls: PropTypes.string,
  dropdownMenuColumnStyle: PropTypes.object,
  builtinPlacements: PropTypes.object,
  loadData: PropTypes.func,
  changeOnSelect: PropTypes.bool,
  children: PropTypes.node,
  onKeyDown: PropTypes.func,
  expandTrigger: PropTypes.string,
  fieldNames: PropTypes.object,
  expandIcon: PropTypes.node,
  loadingIcon: PropTypes.node,
  isFilterOptions: PropTypes.bool
}

export default CommonCascader
